/**
 * Transfer entry — `/inventory/transfers/new`.
 *
 * ONE form produces the whole document. `immediate` posts the outgoing and
 * incoming legs atomically under one shared `transfer_id`; `in_transit` posts
 * only the outgoing legs and leaves the receive for later (its transit
 * warehouse and bin are workflow provenance, required by the contract in that
 * mode); `draft` posts nothing and is the only mode the PATCH flow accepts
 * afterwards.
 *
 * There is never a second "incoming" form — a half-posted transfer cannot
 * exist.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { formatDateTime } from "@/lib/format";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  QUANTITY_FRACTION_DIGITS,
  TRANSFER_MODES,
  createTransfer,
  type TransferCreate,
  type TransferCreateResult,
  type TransferPostingMode,
} from "../api";
import { isPositiveDecimal, toScaledDecimalString } from "../decimal";
import {
  asBeforeLatestMovementProblem,
  asInsufficientStockProblem,
  asPeriodClosedProblem,
  asUomUnconvertibleProblem,
  type OccurredAtBeforeLatestMovementProblem,
  type OccurredAtPeriodClosedProblem,
} from "../problems";
import { useWarehouseOptions } from "../queries";
import { BatchSelect, LocationSelect, UomSelect, VariantPicker } from "../components/pickers";
import { ScanBox } from "../components/scan-box";

interface LineDraft {
  key: string;
  variantId: string | null;
  variantSku: string | null;
  qty: string;
  uomId: string | null;
  batchId: string | null;
}

function emptyLine(): LineDraft {
  return {
    key: `line-${Math.random().toString(36).slice(2)}`,
    variantId: null,
    variantSku: null,
    qty: "",
    uomId: null,
    batchId: null,
  };
}

export interface TransferEntryPageProps {
  canReadVariants?: boolean;
  canReadUoms?: boolean;
  canReadBatches?: boolean;
  canScan?: boolean;
  onCreated?: (transferId: string) => void;
  onOpenLegs?: (transferId: string) => void;
  onCancel?: () => void;
}

export function TransferEntryPage({
  canReadVariants = false,
  canReadUoms = false,
  canReadBatches = false,
  canScan = false,
  onCreated,
  onOpenLegs,
  onCancel,
}: TransferEntryPageProps) {
  const { t, i18n } = useTranslation();

  const [sourceWarehouseId, setSourceWarehouseId] = useState<string | null>(null);
  const [destinationWarehouseId, setDestinationWarehouseId] = useState<string | null>(null);
  const [mode, setMode] = useState<TransferPostingMode>("immediate");
  const [transitWarehouseId, setTransitWarehouseId] = useState<string | null>(null);
  const [transitLocationId, setTransitLocationId] = useState<string | null>(null);
  const [occurredAt, setOccurredAt] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [formError, setFormError] = useState<string | null>(null);
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  const [periodClosed, setPeriodClosed] = useState<OccurredAtPeriodClosedProblem | null>(null);
  const [beforeLatest, setBeforeLatest] = useState<OccurredAtBeforeLatestMovementProblem | null>(
    null,
  );
  const [result, setResult] = useState<TransferCreateResult | null>(null);

  const warehousesQuery = useWarehouseOptions();
  const warehouses = useMemo(() => warehousesQuery.data ?? [], [warehousesQuery.data]);
  const sameWarehouse = sourceWarehouseId !== null && sourceWarehouseId === destinationWarehouseId;

  const clearProblems = () => {
    setFormError(null);
    setLineErrors({});
    setPeriodClosed(null);
    setBeforeLatest(null);
  };

  const mutation = useIdempotentMutation<TransferCreateResult, TransferCreate>({
    mutationFn: (input, key) => createTransfer(input, key),
    onSuccess: (created) => {
      clearProblems();
      // Stay on the form: the confirmation panel names the shared transfer id
      // and both ledger legs before the operator leaves for the document.
      setResult(created);
      setLines([emptyLine()]);
    },
    onError: (error) => {
      const period = asPeriodClosedProblem(error);
      if (period !== null) {
        setPeriodClosed(period);
        return;
      }
      const latest = asBeforeLatestMovementProblem(error);
      if (latest !== null) {
        setBeforeLatest(latest);
        return;
      }
      const shortage = asInsufficientStockProblem(error);
      if (shortage !== null) {
        setFormError(
          shortage.detail ??
            t(
              "inventory.transferEntry.insufficientStock",
              "The source warehouse does not hold enough of one line to ship it.",
            ),
        );
        return;
      }
      const uom = asUomUnconvertibleProblem(error);
      if (uom !== null) {
        setFormError(
          t(
            "inventory.problems.uomUnconvertible",
            "This unit has no conversion to the item's base unit. Add the conversion in Catalog first.",
          ),
        );
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const buildRequest = (): TransferCreate | null => {
    if (sourceWarehouseId === null || destinationWarehouseId === null) {
      setFormError(
        t("inventory.transferEntry.warehousesRequired", "Choose both the source and destination."),
      );
      return null;
    }
    if (sameWarehouse) {
      setFormError(
        t(
          "inventory.transferEntry.sameWarehouse",
          "Source and destination must be different warehouses.",
        ),
      );
      return null;
    }
    if (mode === "in_transit" && (transitWarehouseId === null || transitLocationId === null)) {
      setFormError(
        t(
          "inventory.transferEntry.transitRequired",
          "A two-step transfer has to name the transit warehouse and bin.",
        ),
      );
      return null;
    }

    const nextLineErrors: Record<string, string> = {};
    const payloadLines = lines.map((line, index) => {
      if (line.variantId === null || line.uomId === null) {
        nextLineErrors[line.key] = t(
          "inventory.transferEntry.lineIncomplete",
          "Choose a SKU and a unit for this line.",
        );
        return null;
      }
      const scaled = toScaledDecimalString(line.qty, QUANTITY_FRACTION_DIGITS);
      if (scaled === null || !isPositiveDecimal(scaled)) {
        nextLineErrors[line.key] = t(
          "inventory.transferEntry.lineQty",
          "Enter a quantity above zero with at most 6 decimal places.",
        );
        return null;
      }
      return {
        line_number: index + 1,
        variant_id: line.variantId,
        uom_id: line.uomId,
        qty: scaled,
        ...(line.batchId === null ? {} : { batch_id: line.batchId }),
      };
    });

    if (Object.keys(nextLineErrors).length > 0) {
      setLineErrors(nextLineErrors);
      return null;
    }

    const occurredIso = occurredAt === "" ? null : new Date(occurredAt).toISOString();

    const base = {
      source_warehouse_id: sourceWarehouseId,
      destination_warehouse_id: destinationWarehouseId,
      lines: payloadLines.filter((line): line is NonNullable<typeof line> => line !== null),
      ...(occurredIso === null ? {} : { occurred_at: occurredIso }),
    };

    if (mode === "in_transit") {
      return {
        ...base,
        mode: "in_transit",
        transit_warehouse_id: transitWarehouseId as string,
        transit_location_id: transitLocationId as string,
      };
    }
    if (mode === "draft") {
      return { ...base, mode: "draft" };
    }
    return { ...base, mode: "immediate" };
  };

  const submit = () => {
    clearProblems();
    const request = buildRequest();
    if (request === null) return;
    mutation.mutate(request);
  };

  const warehouseOptions = useMemo(
    () =>
      warehouses.map((warehouse) => ({
        id: warehouse.id,
        label: `${warehouse.code} — ${warehouse.name}`,
      })),
    [warehouses],
  );

  const modeHint =
    mode === "immediate"
      ? t(
          "inventory.transferModeHint.immediate",
          "Both legs post now; the document lands as received.",
        )
      : mode === "in_transit"
        ? t(
            "inventory.transferModeHint.in_transit",
            "Only the outgoing legs post now; receive them at the destination later.",
          )
        : t(
            "inventory.transferModeHint.draft",
            "Nothing posts yet. A draft is the only transfer you can still edit.",
          );

  return (
    <div className="flex max-w-5xl flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">
          {t("inventory.transferEntry.title", "New transfer")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "inventory.transferEntry.subtitle",
            "One document, both ledger legs. Nothing here can leave stock in limbo.",
          )}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("inventory.transferEntry.route", "Route")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-[1fr_auto_1fr]">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="transfer-source">
                {t("inventory.transfers.columns.from", "From")}
              </Label>
              <Select
                value={sourceWarehouseId ?? ""}
                onValueChange={(value) => setSourceWarehouseId(value)}
              >
                <SelectTrigger id="transfer-source">
                  <SelectValue placeholder={t("inventory.common.pickWarehouse", "Warehouse…")} />
                </SelectTrigger>
                <SelectContent>
                  {warehouseOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <ArrowRight
              className="mx-auto mb-2 size-5 text-muted-foreground rtl:rotate-180"
              aria-hidden="true"
            />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="transfer-destination">
                {t("inventory.transfers.columns.to", "To")}
              </Label>
              <Select
                value={destinationWarehouseId ?? ""}
                onValueChange={(value) => setDestinationWarehouseId(value)}
              >
                <SelectTrigger id="transfer-destination" aria-invalid={sameWarehouse}>
                  <SelectValue placeholder={t("inventory.common.pickWarehouse", "Warehouse…")} />
                </SelectTrigger>
                <SelectContent>
                  {warehouseOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {sameWarehouse ? (
                <p role="alert" className="text-sm text-destructive">
                  {t(
                    "inventory.transferEntry.sameWarehouse",
                    "Source and destination must be different warehouses.",
                  )}
                </p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="transfer-mode">{t("inventory.transferEntry.mode", "Posting")}</Label>
              <Select value={mode} onValueChange={(value) => setMode(value as TransferPostingMode)}>
                <SelectTrigger id="transfer-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSFER_MODES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`inventory.transferMode.${value}`, value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{modeHint}</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="transfer-occurred-at">
                {t("inventory.common.occurredAt", "Business time")}
              </Label>
              <Input
                id="transfer-occurred-at"
                type="datetime-local"
                value={occurredAt}
                onChange={(event) => setOccurredAt(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "inventory.transferEntry.occurredAtHint",
                  "Leave empty to use the server's transaction time.",
                )}
              </p>
            </div>

            {mode === "in_transit" ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="transfer-transit-warehouse">
                    {t("inventory.transferEntry.transitWarehouse", "Transit warehouse")}
                  </Label>
                  <Select
                    value={transitWarehouseId ?? ""}
                    onValueChange={(value) => {
                      setTransitWarehouseId(value);
                      setTransitLocationId(null);
                    }}
                  >
                    <SelectTrigger id="transfer-transit-warehouse">
                      <SelectValue
                        placeholder={t("inventory.common.pickWarehouse", "Warehouse…")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouseOptions.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="transfer-transit-location">
                    {t("inventory.transferEntry.transitLocation", "Transit bin")}
                  </Label>
                  <LocationSelect
                    id="transfer-transit-location"
                    warehouseId={transitWarehouseId}
                    value={transitLocationId}
                    onChange={setTransitLocationId}
                  />
                </div>
              </>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3">
          <CardTitle>
            <h2>{t("inventory.transferEntry.lines", "Lines")}</h2>
          </CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setLines((current) => [...current, emptyLine()])}
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("inventory.transferEntry.addLine", "Add line")}
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <ScanBox
            id="transfer-scan"
            canResolve={canScan}
            label={t("inventory.transferEntry.scan", "Scan to fill the last line")}
            onResolved={(variant) => {
              const last = lines[lines.length - 1];
              if (last === undefined) return;
              updateLine(last.key, { variantId: variant.id, variantSku: variant.sku });
            }}
          />

          {lines.map((line, index) => (
            <div
              key={line.key}
              className="grid grid-cols-1 gap-3 rounded-md border p-3 sm:grid-cols-2 lg:grid-cols-4"
            >
              <div className="flex flex-col gap-1.5 lg:col-span-2">
                <Label htmlFor={`transfer-line-${index}-variant`}>
                  {t("inventory.common.sku", "SKU")}
                </Label>
                <VariantPicker
                  id={`transfer-line-${index}-variant`}
                  value={line.variantId}
                  selectedSku={line.variantSku}
                  canRead={canReadVariants}
                  onChange={(variantId, sku) =>
                    updateLine(line.key, { variantId, variantSku: sku, batchId: null })
                  }
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`transfer-line-${index}-qty`}>
                  {t("inventory.common.qty", "Quantity")}
                </Label>
                <Input
                  id={`transfer-line-${index}-qty`}
                  dir="ltr"
                  inputMode="decimal"
                  className="text-end"
                  value={line.qty}
                  aria-invalid={lineErrors[line.key] !== undefined}
                  onChange={(event) => updateLine(line.key, { qty: event.target.value })}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`transfer-line-${index}-uom`}>
                  {t("inventory.common.uom", "Unit")}
                </Label>
                <UomSelect
                  id={`transfer-line-${index}-uom`}
                  value={line.uomId}
                  canRead={canReadUoms}
                  onChange={(uomId) => updateLine(line.key, { uomId })}
                />
              </div>
              {canReadBatches ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`transfer-line-${index}-batch`}>
                    {t("inventory.common.batch", "Batch")}
                  </Label>
                  <BatchSelect
                    id={`transfer-line-${index}-batch`}
                    variantId={line.variantId}
                    warehouseId={sourceWarehouseId}
                    value={line.batchId}
                    canRead={canReadBatches}
                    onChange={(batchId) => updateLine(line.key, { batchId })}
                  />
                </div>
              ) : null}
              <div className="flex items-end lg:col-span-4">
                {lines.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setLines((current) =>
                        current.filter((candidate) => candidate.key !== line.key),
                      )
                    }
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                    {t("inventory.transferEntry.removeLine", "Remove line")}
                  </Button>
                ) : null}
              </div>
              {lineErrors[line.key] !== undefined ? (
                <p role="alert" className="text-sm text-destructive lg:col-span-4">
                  {lineErrors[line.key]}
                </p>
              ) : null}
            </div>
          ))}

          {periodClosed !== null ? (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
            >
              <p className="font-medium">
                {t("inventory.problems.periodClosedTitle", "That date is in a closed period")}
              </p>
              <p className="text-muted-foreground">
                {t(
                  "inventory.problems.periodClosedBody",
                  "Period {{period}} is {{state}}. A movement has to be dated inside an open period.",
                  {
                    period: periodClosed.period,
                    state: t(
                      `inventory.periodState.${periodClosed.period_state}`,
                      periodClosed.period_state,
                    ),
                  },
                )}
              </p>
            </div>
          ) : null}

          {beforeLatest !== null ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
            >
              <p className="font-medium">
                {t(
                  "inventory.problems.beforeLatestTitle",
                  "That date is before the latest movement",
                )}
              </p>
              <p className="text-muted-foreground">
                {t(
                  "inventory.problems.beforeLatestBody",
                  "{{warehouse}} already has a movement for this item at {{latest}}. Backdating before it would rewrite cost history, which this ledger does not do.",
                  {
                    warehouse: beforeLatest.warehouse_code,
                    latest: formatDateTime(beforeLatest.latest_occurred_at, i18n.language),
                  },
                )}
              </p>
            </div>
          ) : null}

          {formError !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {formError}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              loading={mutation.isPending}
              disabled={mutation.isPending}
              onClick={submit}
            >
              {t("inventory.transferEntry.submit", "Create transfer")}
            </Button>
            {onCancel !== undefined ? (
              <Button type="button" variant="ghost" onClick={onCancel}>
                {t("actions.cancel")}
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {result !== null ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("inventory.transferEntry.created", "Transfer created")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">
                {t(`inventory.transferStatus.${result.status}`, result.status)}
              </Badge>
              <Badge variant="secondary">
                {t(`inventory.transferMode.${result.mode}`, result.mode)}
              </Badge>
              <span dir="ltr" className="font-mono text-xs text-muted-foreground">
                {result.transfer_id}
              </span>
            </div>
            <p className="text-muted-foreground">
              {t("inventory.transferEntry.legs", "{{count}} ledger legs posted.", {
                count: result.movements.length,
              })}
            </p>
            <div className="flex flex-wrap gap-2">
              {onOpenLegs !== undefined && result.movements.length > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenLegs(result.transfer_id)}
                >
                  {t("inventory.transferEntry.viewLegs", "View both legs")}
                </Button>
              ) : null}
              {onCreated !== undefined ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onCreated(result.transfer_id)}
                >
                  {t("inventory.transferEntry.openDocument", "Open the transfer")}
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
